import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useFetcher } from 'react-router';
import { actions, type AuthResponse, events, getFieldErrorMessage, views } from '@cx-web/feature-auth';
import { CX_LOGIN_SUCCESS } from '@cx-web/feature-auth/events';
import { trackSignupFailure, trackSignupSuccess } from '@cx-web/feature-auth/tracking';
import {
  NON_PLUS_AUTH_URLS_CONFIG,
  REPUB_CONFIG,
  SHOW_AGREE_TERMS_AS_CHECKBOX_CONFIG,
  SHOW_MARKETING_CONSENT_CONFIG,
  SIGN_IN_IMMEDIATELY_AFTER_SIGNUP_CONFIG,
} from '@cx-web/repub-config';
import { trackEvent } from '@cx-web/utils/dataLayer';
import { Button } from '@fpsm/cxy-components/Button';
import { Checkbox } from '@fpsm/cxy-components/Checkbox';
import { Input } from '@fpsm/cxy-components/Input';

import { type Repub } from '@cx-modules/constants';

import { type OpenAtViewOptions, type View, VIEW_VERIFY_EMAIL } from '../../../auth-modal-provider';

import styles from './styles/SignupForm.module.css';

export type SignupFormProps = {
  authBasePath?: string;
  close: () => void;
  // Whether this render is the non-plus flow (see Authentication's own prop). Drives
  // showAgreeTermsAsCheckbox and the terms/privacy URLs, both of which fall back to the plus-only
  // config for plus signups; showMarketingConsent is repub-driven regardless of this flag. Also
  // gates the post-signup VerifyEmail screen (see the api-success effect below) — that screen is
  // the since-deleted myoc-login-2018 widget's behaviour, which only ever served non-plus consumers; plus
  // had its own signup flow that already closed + dispatched immediately, so plus must keep doing
  // that unchanged (EU-5591).
  hideInfoPanel?: boolean;
  openAtView: (view: View, options?: OpenAtViewOptions) => void;
  // Seeds the email field when the modal was opened with a known email (e.g. the onboarding
  // newsletter CTA). The typed replacement for the legacy `#myoc-login-modal input[type=email]`
  // DOM-scrape: the value now arrives as a prop instead of being written into the widget DOM.
  prefillEmail?: string;
  repub: Repub;
  repubPath: string;
};

export const SignupForm = ({
  authBasePath = '/plus/auth',
  close,
  hideInfoPanel,
  openAtView,
  prefillEmail,
  repub,
  repubPath,
}: SignupFormProps) => {
  // Repub-driven, not plus/non-plus-driven (EU-5591): whether to ask for marketing consent is a
  // per-repub legal/product decision, not a function of which surface renders the form.
  const showMarketingConsent = SHOW_MARKETING_CONSENT_CONFIG[repub];
  // Non-plus rollout (EU-5591): showAgreeTermsAsCheckbox has no plus config to read for most
  // repubs, so non-plus renders use a simpler repub-only config instead of the plus-only source.
  const showAgreeTermsAsCheckbox = hideInfoPanel
    ? SHOW_AGREE_TERMS_AS_CHECKBOX_CONFIG[repub]
    : REPUB_CONFIG[repub]?.plus?.auth.showAgreeTermsAsCheckbox;
  // Non-plus rollout (EU-5591): most repubs have no plus config at all, so the terms/privacy URLs
  // read here for non-plus signups instead, independent of plus's own values.
  const authUrls = hideInfoPanel ? NON_PLUS_AUTH_URLS_CONFIG[repub] : REPUB_CONFIG[repub]?.plus?.auth;
  const fetcher = useFetcher<AuthResponse>();
  const signupSuccessRef = useRef(false);
  // Captured on submit so the success effect can seed VerifyEmail's displayed address — the form is
  // uncontrolled, so this is the only place the submitted value is available once fetcher.data comes
  // back (the signup response itself doesn't echo the email).
  const submittedEmailRef = useRef('');
  const [showErrorState, setShowErrorState] = useState(false);

  const { t } = useTranslation(['label']);

  const data = fetcher.data;
  // Two distinct failure shapes come back from the signup action (apps/atlas/app/templates/auth/signup.tsx):
  // client-side validation failures return `{ fieldErrors, success: false }` with no top-level `message`,
  // while MyOC/register failures return `{ message, success: false }` with no `fieldErrors`. Both must be
  // handled without assuming the other is present.
  const hasFieldErrors = Boolean(data?.fieldErrors && Object.values(data.fieldErrors).length > 0);
  const hasGenericError = Boolean(data && !data.success && data.message);
  const hasErrorMessage = hasFieldErrors || hasGenericError;
  const isAgreeTermsError = showErrorState && Boolean(getFieldErrorMessage(data?.fieldErrors?.agreeTerms));

  // handle local error
  useEffect(() => {
    if (fetcher.state === 'idle' && hasErrorMessage) {
      setShowErrorState(true);
    }
  }, [hasErrorMessage, fetcher.state]);

  // handle api success
  useEffect(() => {
    if (data?.success && !signupSuccessRef.current) {
      signupSuccessRef.current = true;

      trackSignupSuccess('auth_modal', { repub, userId: data.userId! });

      // Non-plus (atlas/atlas-astro) repub-driven (EU-5591): most repubs sign in immediately just
      // like plus; only repubs with SIGN_IN_IMMEDIATELY_AFTER_SIGNUP_CONFIG[repub] === false
      // (currently just OC) route to VerifyEmail instead, restoring the since-deleted myoc-login-2018
      // DoneView behaviour for that repub. Plus (hideInfoPanel undefined) always signs in
      // immediately regardless of this config, since plus never shows the verify-email screen.
      const shouldShowVerifyEmail = hideInfoPanel && !SIGN_IN_IMMEDIATELY_AFTER_SIGNUP_CONFIG[repub];

      if (shouldShowVerifyEmail) {
        // This repub's signup action deliberately does NOT authenticate/set a session cookie (see
        // apps/atlas/app/templates/auth/signup.tsx) — the whole point of this screen is that the
        // account isn't usable until the user verifies their email and logs in for real, so no
        // CX_LOGIN_SUCCESS/optimistic-login-state dispatch here either.
        openAtView(VIEW_VERIFY_EMAIL, { prefillEmail: submittedEmailRef.current });
      } else {
        // The signup action (atlas's or product-plus's) authenticated and set a session cookie
        // immediately, so the header's optimistic state should reflect that real login.
        window.dispatchEvent(new CustomEvent(CX_LOGIN_SUCCESS, { detail: data }));
        close();
      }
    }
  }, [close, data, hideInfoPanel, openAtView, repub]);

  // handle api error
  useEffect(() => {
    if (fetcher.state === 'idle' && hasErrorMessage && data) {
      trackSignupFailure('auth_modal', {
        error:
          data.message ??
          Object.values(data.fieldErrors ?? {})
            .map(getFieldErrorMessage)
            .filter(Boolean)
            .join(', '),
        repub,
      });
    }
  }, [fetcher.state, hasErrorMessage, data, repub]);

  const onChange = () => {
    setShowErrorState(false);
  };

  return (
    <fetcher.Form
      action={`${repubPath}${authBasePath}/signup`}
      className={styles.signUpForm}
      data-testid="signup-form"
      method="post"
      onSubmit={e => {
        submittedEmailRef.current = String(new FormData(e.currentTarget).get('email') ?? '');

        trackEvent(events.ACCOUNT_CREATED_ATTEMPT, {
          eventAction: actions.SIGN_UP,
          method: REPUB_CONFIG[repub]?.tracking.authProvider,
          view: views.SIGN_UP,
        });
      }}
    >
      <Input
        aria-describedby="email-error"
        autoComplete="email"
        className={styles.input}
        // `key` is keyed off prefillEmail so a change in the seeded value remounts the uncontrolled
        // input and its defaultValue takes effect (React only reads defaultValue on mount).
        defaultValue={prefillEmail}
        errors={
          showErrorState ? ([getFieldErrorMessage(data?.fieldErrors?.email)].filter(Boolean) as string[]) : undefined
        }
        id="email"
        key={`email-${prefillEmail ?? ''}`}
        name="email"
        onChange={onChange}
        placeholder={t('CX_AUTH_SIGNUP_PLACEHOLDER_EMAIL')}
        type="email"
      />
      <Input
        aria-describedby="password-error"
        autoComplete="new-password"
        id="password"
        key="password"
        name="password"
        onChange={onChange}
        placeholder={t('CX_AUTH_SIGNUP_PLACEHOLDER_PASSWORD')}
        type="password"
      />
      <Input
        aria-describedby="confirmPassword-error"
        autoComplete="new-password"
        errors={
          showErrorState
            ? ([
                getFieldErrorMessage(data?.fieldErrors?.password),
                getFieldErrorMessage(data?.fieldErrors?.confirmPassword),
              ].filter(Boolean) as string[])
            : undefined
        }
        id="confirmPassword"
        key="confirmPassword"
        name="confirmPassword"
        onChange={onChange}
        placeholder={t('CX_AUTH_SIGNUP_PLACEHOLDER_CONFIRM_PASSWORD')}
        type="password"
      />
      {hasGenericError && showErrorState && (
        <div className={styles.errorMessage} id="generic-error">
          {data?.message}
        </div>
      )}
      {showAgreeTermsAsCheckbox && showErrorState && getFieldErrorMessage(data?.fieldErrors?.agreeTerms) && (
        <div className={styles.agreeTermsError} id="agreeTerms-error">
          {getFieldErrorMessage(data?.fieldErrors?.agreeTerms)}
        </div>
      )}
      <div className={styles.buttonSection}>
        <Button isLoading={fetcher.state !== 'idle'} size="large" type="submit" variant="secondary">
          {t('CX_AUTH_SIGNUP')}
        </Button>
      </div>
      {showAgreeTermsAsCheckbox && (
        <div className={styles.termsContainer}>
          <div className={styles.termsCheckboxRow}>
            <Checkbox id="agreeTerms" name="agreeTerms" onChange={onChange} value="true" />
            <p className={isAgreeTermsError ? styles.marketingConsentTextError : styles.marketingConsentText}>
              <Trans
                components={{
                  privacyLink: (
                    // eslint-disable-next-line jsx-a11y/anchor-has-content -- Trans injects the link text at runtime
                    <a
                      className={isAgreeTermsError ? styles.termsLinkError : styles.termsLink}
                      href={authUrls?.privacyPolicyUrl(repubPath)}
                    />
                  ),
                  termsLink: (
                    // eslint-disable-next-line jsx-a11y/anchor-has-content -- Trans injects the link text at runtime
                    <a
                      className={isAgreeTermsError ? styles.termsLinkError : styles.termsLink}
                      href={authUrls?.termsAndConditionsUrl(repubPath)}
                    />
                  ),
                }}
                i18nKey="CX_AUTH_TERMS_AGREEMENT_CONFIRMATION"
              />
            </p>
          </div>
        </div>
      )}
      {showMarketingConsent && (
        <Checkbox id="marketingConsent" name="marketingConsent" value="true">
          <p className={styles.marketingConsentText}>
            {t('CX_AUTH_YES_TO_MARKETING', { name: REPUB_CONFIG[repub]?.plus?.name })}
          </p>
        </Checkbox>
      )}
      {!showAgreeTermsAsCheckbox && (
        <p className={styles.termsText}>
          <Trans
            components={{
              // eslint-disable-next-line jsx-a11y/anchor-has-content -- Trans injects the link text at runtime
              privacyLink: <a href={authUrls?.privacyPolicyUrl(repubPath)} />,
              // eslint-disable-next-line jsx-a11y/anchor-has-content -- Trans injects the link text at runtime
              termsLink: <a href={authUrls?.termsAndConditionsUrl(repubPath)} />,
            }}
            i18nKey="CX_AUTH_TERMS_AGREEMENT_CONFIRMATION"
          />
        </p>
      )}
    </fetcher.Form>
  );
};

SignupForm.displayName = 'SignupForm';
